import React, { useEffect, useRef, useState } from 'react';
import SuccessToast from '../SuccessToast';
import FichaContacto from './FichaContacto';
import { AvatarContacto, CanalContacto } from './PiezasContacto';
import { cambiarCliente, listarContactos } from './contactosApi';
import { detalleDeError, fechaLocal, haceCuanto, identificadorDe, nombreDe } from '../../utils/contactos';

const reemplazar = (datos, chatId, cambio) => {
  if (!datos?.content) return datos;
  return {
    ...datos,
    content: datos.content.map((c) => (c.chatId === chatId ? { ...c, ...cambio } : c)),
  };
};

const recuento = (total, busqueda) => {
  if (busqueda) return total === 1 ? '1 resultado' : `${total} resultados`;
  return total === 1 ? '1 contacto' : `${total} contactos`;
};

function ContactosPanel({ botId, isAdmin = false, contactoInicial = null, onVolverAlChat = null }) {
  const [abierto, setAbierto] = useState(contactoInicial);
  const [soloClientes, setSoloClientes] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [texto, setTexto] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [toastExito, setToastExito] = useState(null);
  const [toastError, setToastError] = useState(null);
  const guardandoRef = useRef(new Set());
  const [guardando, setGuardando] = useState(() => new Set());

  useEffect(() => {
    if (botId == null || abierto != null) return undefined;
    let vivo = true;
    setDatos(null);
    setError(null);
    listarContactos(botId, { page: pagina, soloClientes, q: busqueda }).then((res) => {
      if (!vivo) return;
      if (!res.ok) {
        setError(detalleDeError(res, 'No se pudieron cargar los contactos'));
        setDatos({ content: [] });
        return;
      }
      setDatos(res.data);
    });
    return () => {
      vivo = false;
    };
  }, [botId, pagina, soloClientes, busqueda, abierto]);

  const buscar = (event) => {
    event.preventDefault();
    setPagina(0);
    setBusqueda(texto.trim());
  };

  const limpiar = () => {
    setTexto('');
    setPagina(0);
    setBusqueda('');
  };

  const alternar = async (contacto) => {
    if (guardandoRef.current.has(contacto.chatId)) return;
    const nuevo = !contacto.cliente;
    const enCurso = new Set(guardandoRef.current);
    enCurso.add(contacto.chatId);
    guardandoRef.current = enCurso;
    setGuardando(enCurso);
    setDatos((prev) => reemplazar(prev, contacto.chatId, { cliente: nuevo }));
    const res = await cambiarCliente(botId, contacto.chatId, nuevo);
    const siguiente = new Set(guardandoRef.current);
    siguiente.delete(contacto.chatId);
    guardandoRef.current = siguiente;
    setGuardando(siguiente);
    if (!res.ok) {
      setDatos((prev) => reemplazar(prev, contacto.chatId, { cliente: contacto.cliente }));
      setToastExito(null);
      setToastError(detalleDeError(res, 'No se pudo cambiar'));
      return;
    }
    setDatos((prev) => reemplazar(prev, contacto.chatId, res.data));
    setToastError(null);
    setToastExito(res.data.cliente ? 'Marcado como cliente' : 'Ya no es cliente');
  };

  if (abierto != null) {
    const vuelveAlChat = onVolverAlChat != null && abierto === contactoInicial;
    return (
      <FichaContacto
        botId={botId}
        chatId={abierto}
        isAdmin={isAdmin}
        onVolver={vuelveAlChat ? onVolverAlChat : () => setAbierto(null)}
        etiquetaVolver={vuelveAlChat ? 'Volver al chat' : 'Volver a contactos'}
      />
    );
  }

  const contactos = datos?.content ?? [];

  return (
    <div className="contactos-panel">
      <SuccessToast message={toastExito} onDismiss={() => setToastExito(null)} />
      <SuccessToast message={toastError} variant="error" onDismiss={() => setToastError(null)} />
      <div className="contactos-cabecera">
        <div className="contactos-titulo">
          <h2>Contactos</h2>
          {datos && !error ? (
            <span className="contactos-recuento">{recuento(datos.totalElements ?? contactos.length, busqueda)}</span>
          ) : null}
        </div>
        <label className={`contactos-filtro${soloClientes ? ' activo' : ''}`}>
          <input
            type="checkbox"
            checked={soloClientes}
            onChange={(event) => {
              setPagina(0);
              setSoloClientes(event.target.checked);
            }}
          />
          Solo clientes
        </label>
      </div>
      <form className="contactos-busqueda" role="search" onSubmit={buscar}>
        <div className="contactos-busqueda-caja">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            aria-label="Buscar por teléfono o nombre"
            placeholder="Buscar por teléfono o nombre"
            value={texto}
            onChange={(event) => setTexto(event.target.value)}
          />
        </div>
        <button type="submit" className="admin-btn primary">
          Buscar
        </button>
        {busqueda ? (
          <button type="button" className="admin-btn secondary" onClick={limpiar}>
            Limpiar
          </button>
        ) : null}
      </form>
      {datos === null ? (
        <div className="contactos-cargando">
          <p className="config-card-hint">Cargando contactos…</p>
          <div className="contactos-esqueleto" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        </div>
      ) : null}
      {error ? <p className="vars-aviso">{error}</p> : null}
      {datos && !error && contactos.length === 0 ? (
        <div className="contactos-vacio">
          <AvatarContacto contacto={{ chatId: 0 }} />
          <p>{busqueda ? `Ningún contacto coincide con «${busqueda}»` : 'Este bot aún no tiene contactos'}</p>
          <span>
            {busqueda
              ? 'Prueba con otra parte del número o del nombre.'
              : 'Aparecerán aquí en cuanto alguien escriba por WhatsApp o Instagram.'}
          </span>
        </div>
      ) : null}
      {contactos.length > 0 ? (
        <table className="contactos-tabla">
          <thead>
            <tr>
              <th scope="col">Contacto</th>
              <th scope="col">Canal</th>
              <th scope="col">Último mensaje</th>
              <th scope="col">Cliente</th>
            </tr>
          </thead>
          <tbody>
            {contactos.map((contacto) => (
              <tr
                key={contacto.chatId}
                className={contacto.cliente ? 'es-cliente' : undefined}
                onClick={() => setAbierto(contacto.chatId)}
              >
                <td data-label="Contacto" className="contactos-celda-persona">
                  <AvatarContacto contacto={contacto} />
                  <div className="contactos-persona">
                    <button
                      type="button"
                      className="contactos-nombre"
                      aria-label={`Ver ficha de ${nombreDe(contacto)}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setAbierto(contacto.chatId);
                      }}
                    >
                      {nombreDe(contacto)}
                    </button>
                    <span className="contactos-sub">{identificadorDe(contacto)}</span>
                  </div>
                </td>
                <td data-label="Canal">
                  <CanalContacto plataforma={contacto.plataforma} />
                </td>
                <td data-label="Último mensaje">
                  <span className="contactos-fecha" title={fechaLocal(contacto.ultimoMensaje)}>
                    {haceCuanto(contacto.ultimoMensaje)}
                  </span>
                </td>
                <td data-label="Cliente" onClick={(event) => event.stopPropagation()}>
                  <input
                    type="checkbox"
                    role="switch"
                    className="contactos-switch"
                    aria-label={`Cliente: ${nombreDe(contacto)}`}
                    checked={contacto.cliente}
                    disabled={guardando.has(contacto.chatId)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                      event.stopPropagation();
                      alternar(contacto);
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {datos && (datos.hasPrevious || datos.hasNext) ? (
        <nav className="contactos-paginacion" aria-label="Páginas de contactos">
          <button
            type="button"
            className="admin-btn secondary"
            disabled={!datos.hasPrevious}
            onClick={() => setPagina((p) => Math.max(0, p - 1))}
          >
            <span aria-hidden="true">← </span>Anterior
          </button>
          {datos.totalPages ? (
            <span className="contactos-pagina">
              Página {(datos.page ?? 0) + 1} de {datos.totalPages}
            </span>
          ) : null}
          <button
            type="button"
            className="admin-btn secondary"
            disabled={!datos.hasNext}
            onClick={() => setPagina((p) => p + 1)}
          >
            Siguiente<span aria-hidden="true"> →</span>
          </button>
        </nav>
      ) : null}
    </div>
  );
}

export default ContactosPanel;
